import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Bot, ChevronRight } from "lucide-react";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { PageHeader } from "../components/layout";

function formatDate(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function AccountPage() {
  const { id } = useParams();
  const { token } = useAdminAuth();

  const account = useQuery({
    queryKey: ["admin", "account", id],
    queryFn: () => adminApi.account(token!, id!),
    enabled: Boolean(token && id),
  });

  const funnels = useQuery({
    queryKey: ["admin", "account", id, "funnels"],
    queryFn: () => adminApi.accountFunnels(token!, id!),
    enabled: Boolean(token && id),
  });

  if (account.isLoading || funnels.isLoading) {
    return <div className="panel p-4 text-sm text-muted-foreground">Загружаем аккаунт...</div>;
  }

  if (account.error || funnels.error || !account.data) {
    return <div className="panel p-4 text-sm text-danger">{(account.error ?? funnels.error)?.message}</div>;
  }

  return (
    <div>
      <PageHeader
        title={account.data.amoAccountId}
        description={`Тариф ${account.data.plan}, создан ${formatDate(account.data.createdAt)}`}
        backTo="/accounts"
        backLabel="К списку клиентов"
      />

      <section className="panel mb-4 grid gap-3 p-4 text-sm sm:grid-cols-4">
        <div>
          <p className="text-muted-foreground">Пользователь</p>
          <p className="font-medium">{account.data.user?.email ?? "не привязан"}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Агенты</p>
          <p className="font-medium">{account.data.funnelsCount}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Диалоги</p>
          <p className="font-medium">{account.data.dialogsUsed}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Сброс лимита</p>
          <p className="font-medium">{formatDate(account.data.dialogsResetAt)}</p>
        </div>
      </section>

      <div className="mb-3 flex items-center gap-2">
        <Bot className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Агенты</h2>
      </div>

      <div className="space-y-2">
        {(funnels.data ?? []).map((funnel) => (
          <Link
            key={funnel.id}
            className="panel block p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
            to={`/funnels/${funnel.id}`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{funnel.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  pipeline {funnel.amoPipelineId}
                </p>
              </div>

              <div className="grid min-w-0 grid-cols-2 gap-2 text-sm sm:w-[520px] sm:grid-cols-4">
                <div>
                  <p className="text-muted-foreground">Статус</p>
                  <p className="font-medium">{funnel.active ? "активен" : "выключен"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Модель</p>
                  <p className="font-medium">{funnel.model}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Каналы</p>
                  <p className="font-medium">{funnel.channels.length}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Runs</p>
                  <p className="font-medium">{funnel.runsCount}</p>
                </div>
              </div>

              <ChevronRight className="hidden h-5 w-5 shrink-0 text-muted-foreground sm:block" />
            </div>
          </Link>
        ))}
      </div>

      {funnels.data?.length === 0 ? (
        <div className="panel mt-4 flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <Bot className="h-5 w-5" />
          Агентов пока нет.
        </div>
      ) : null}
    </div>
  );
}
